import { TypedDocumentNode, gql } from '@apollo/client';
import { FC, ReactNode } from 'react';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { toCamelCase } from '@sorare/core/src/lib/toCamelCase';

import { CardPlaceholder } from 'components/lineup/CardPlaceholder';
import { getComposeTeamRoute } from 'lib/so5';

import {
  PlayerCardPlaceholder_managerTeam,
  PlayerCardPlaceholder_so5Leaderboard,
  PlayerCardPlaceholder_so5Lineup,
} from './__generated__/index.graphql';

const Wrapper = styled(LinkOther)`
  & > * {
    transition: transform 200ms ease-in-out;
  }
  &:hover,
  &:focus {
    & > * {
      transform: scale(1.05);
    }
  }
`;

const ORDERED_RARITIES = [
  Rarity.common,
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
] as const;

const getRarityInfo = (
  rarity: Rarity,
  rarityLimit: number,
  cardsCount: number
) => {
  return new Array(Math.max(0, rarityLimit)).fill(rarity).map((_, index) => ({
    rarity,
    available: rarity === Rarity.common || index < cardsCount,
  }));
};

type Props = {
  position: number;
  lineup?: PlayerCardPlaceholder_so5Lineup | null;
  managerTeam?: PlayerCardPlaceholder_managerTeam | null;
  leaderboard: PlayerCardPlaceholder_so5Leaderboard;
  disableLink?: boolean;
  defaultRarity?: Rarity;
  fixtureOpened?: boolean;
  loading?: boolean;
  Footer: FC<{ children?: ReactNode }>;
};

const PlayerCardPlaceholder = ({
  position,
  lineup,
  managerTeam,
  leaderboard,
  disableLink,
  defaultRarity = Rarity.common,
  fixtureOpened,
  loading,
  Footer,
}: Props) => {
  const locked = !leaderboard.canCompose.value;
  const rarityLimits = leaderboard.displayedRules?.rarityLimits;
  const { sport } = useSportContext();
  const { sportProfileBySport } = useCurrentUserContext();

  const sportProfile = sportProfileBySport[sport || Sport.FOOTBALL];

  const displayedRarities = ORDERED_RARITIES.reduce<
    {
      rarity: Rarity;
      available: boolean;
    }[]
  >((acc, rarity) => {
    if (acc.length >= 5) {
      return acc;
    }

    const camelCaseRarity = toCamelCase(rarity);

    const rarityLimit =
      rarityLimits?.[camelCaseRarity]?.min ||
      rarityLimits?.[camelCaseRarity]?.max;
    if (rarityLimit) {
      return [
        ...acc,
        ...getRarityInfo(
          rarity,
          rarityLimit,
          rarity === Rarity.common
            ? 0
            : sportProfile?.cardsCount?.[
                camelCaseRarity as Exclude<typeof camelCaseRarity, 'common'>
              ] || 0
        ),
      ];
    }
    return acc;
  }, []);

  const { rarity = defaultRarity, available } =
    displayedRarities[position] || {};

  const getPlaceholderLink = () => {
    if (locked) {
      return undefined;
    }
    return getComposeTeamRoute({
      leaderboardSlug: leaderboard.slug,
      lineupId: idFromObject(lineup?.id),
      managerTeamId: idFromObject(managerTeam?.id),
      sport,
    });
  };

  const content = (
    <Vertical gap={1}>
      <Skeleton loading={loading}>
        <CardPlaceholder
          rarity={rarity}
          empty={!lineup}
          locked={locked || !fixtureOpened}
          available={!!(available && fixtureOpened)}
          loading={loading}
        />
      </Skeleton>
      <Footer />
    </Vertical>
  );
  return locked || disableLink ? (
    <div>{content}</div>
  ) : (
    <Wrapper as={Link} to={getPlaceholderLink() || ''}>
      {content}
    </Wrapper>
  );
};

PlayerCardPlaceholder.fragments = {
  so5Lineup: gql`
    fragment PlayerCardPlaceholder_so5Lineup on So5Lineup {
      id
    }
  ` as TypedDocumentNode<PlayerCardPlaceholder_so5Lineup>,
  managerTeam: gql`
    fragment PlayerCardPlaceholder_managerTeam on ManagerTeam {
      id
    }
  ` as TypedDocumentNode<PlayerCardPlaceholder_managerTeam>,
  so5Leaderboard: gql`
    fragment PlayerCardPlaceholder_so5Leaderboard on So5Leaderboard {
      slug
      commonDraftCampaign {
        slug
        status
      }
      canCompose {
        value
      }
      displayedRules {
        id
        rarityLimits {
          common {
            min
            max
          }
          limited {
            min
            max
          }
          rare {
            min
            max
          }
          superRare {
            min
            max
          }
          unique {
            min
            max
          }
        }
      }
    }
  ` as TypedDocumentNode<PlayerCardPlaceholder_so5Leaderboard>,
};

export default PlayerCardPlaceholder;
